// components/pages/AgencyPage.js - ROBUST VERSION with better error handling + aligned mobile spacing
// Theme-aware version - uses CSS variables from theme.css
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Image, Breadcrumb, Dropdown, Spinner } from 'react-bootstrap';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Link45deg, People } from 'react-bootstrap-icons';
import FeedSection from '../feed/FeedSection';
import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import NotificationButton from '../notifications/NotificationButton';
import SearchNavBar from '../search/SearchNavBar';
import MobileNavigation from '../navigation/MobileNavigation';
import openCapacitorSite from '../../utils/browserUtils';
import './AgencyPage.css';

// Reusable icon/image component with fallback
// Uses className for theme-aware coloring
const AgencyIcon = ({ image, name, size = 70 }) => {
  const [imgError, setImgError] = useState(false);
  
  if (!image || imgError) {
    return <People size={size} className="agency-icon-fallback" />;
  }
  
  return (
    <Image 
      src={image} 
      alt={name}
      onError={() => setImgError(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

const AgencyPage = ({ country = 'us', maxItemsPerFeed = 1 }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [departmentConfig, setDepartmentConfig] = useState(null);
  const [agencyConfig, setAgencyConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract department and agency from URL
  const extractFromPath = (pathname) => {
    const pathParts = pathname.split('/').filter(Boolean);
    
    if (pathParts.length >= 3) {
      return {
        country: pathParts[0],
        department: pathParts[1],
        agencyId: pathParts[2]
      };
    }
    return null;
  };

  const pathInfo = extractFromPath(location.pathname);
  const department = pathInfo?.department || params.department;
  const agencyId = pathInfo?.agencyId || params.agencyId || params['*'];

  // Load configurations
  useEffect(() => {
    const loadConfigs = async () => {
      setLoading(true);
      setError(null);

      try {
        const { getDepartmentConfig, getAgencyConfig } = await import('../../utils/departmentConfig');
        
        const deptConfig = getDepartmentConfig(department);
        
        if (!deptConfig) {
          setError(`Department "${department}" not found`);
          setLoading(false);
          return;
        }

        setDepartmentConfig(deptConfig);

        const agConfig = getAgencyConfig(department, agencyId);
        
        if (!agConfig) {
          setError(`Agency "${agencyId}" not found in department "${department}"`);
          setLoading(false);
          return;
        }

        setAgencyConfig(agConfig);
        setLoading(false);
      } catch (err) {
        console.error('Error loading configs:', err);
        setError(`Error loading configuration: ${err.message}`);
        setLoading(false);
      }
    };

    if (department && agencyId) {
      loadConfigs();
    } else {
      setError('Missing department or agency ID in URL');
      setLoading(false);
    }
  }, [department, agencyId]);

  // Auto-detect mobile layout
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle agency-level notifications
  const handleAgencyNotificationChange = (isSubscribed, feedId, feedName) => {
    console.log(`Agency notification ${isSubscribed ? 'enabled' : 'disabled'}:`, {
      agencyId,
      feedId,
      feedName,
      agency: agencyConfig?.name,
      department: departmentConfig?.departmentInfo?.name
    });
  };

  // Loading state
  if (loading) {
    return (
      <div id='homebg' className="agency-page-root loading-state">
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">
              <Spinner id='spinner' animation="grow" variant="light" />
            </span>
          </div>
          <p style={{ marginTop: '20px' }}>Loading agency information...</p>
        </div>
      </div>
    );
  }

  // Error states with helpful debugging
  if (error || !departmentConfig || !agencyConfig) {
    return (
      <div id='homebg' className="agency-page-root" style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Page Error</h2>
        <div className="error-state" style={{ marginTop: '20px', textAlign: 'left' }}>
          <p><strong>Error:</strong> {error || 'Configuration not loaded'}</p>
          <p><strong>URL:</strong> {location.pathname}</p>
          <p><strong>Department:</strong> {department || 'Not found'}</p>
          <p><strong>Agency ID:</strong> {agencyId || 'Not found'}</p>
          {departmentConfig && (
            <p><strong>Available Agencies:</strong> {departmentConfig.agencies?.map(a => a.id).join(', ')}</p>
          )}
        </div>
        <Button 
          variant="primary" 
          onClick={() => navigate(`/${country}/executive`)}
          style={{ marginTop: '20px' }}
        >
          Return to Executive Branch
        </Button>
      </div>
    );
  }

  const { departmentInfo } = departmentConfig;

  // -------- MOBILE LAYOUT ----------
  if (isMobile) {
    const agencyFeed = {
      id: agencyConfig.id,
      name: agencyConfig.name,
      displayType: 'normal',
      apiParams: agencyConfig.apiParams,
      feedBranch: 'Executive',
      feedTopic: agencyConfig.topic,
      buttonName: agencyConfig.buttonName || 'Document'
    };
    
    const mobileFeedsData = [agencyFeed];

    return (
      <div id="homebg" className="agency-page-root">
        
        {/* Match HomePage spacing - 70px navbar clearance */}
        <br></br>
        <div style={{ marginTop: '45px', paddingTop: '5px' }}>
          <div id="feedDisplayDepartment">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: '8px 10px' 
            }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <div id="profileImageOnPageDepartment">
                  <AgencyIcon 
                    image={agencyConfig.image} 
                    name={agencyConfig.name} 
                    size={45}
                  />
                </div>
                <div style={{ 
                  marginLeft: '10px', 
                  fontWeight: 'bold', 
                  display: 'flex', 
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                  paddingRight: '10px'
                }}>
                  <h5 
                    id="branchHeadingMobile"
                    style={{
                      fontSize: 'clamp(12px, 3.5vw, 17px)',
                      lineHeight: '1.2',
                      margin: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      transform: `scale(${Math.max(0.7, Math.min(1, 25 / Math.max(agencyConfig.name.length, 10)))})`,
                      transformOrigin: 'left center'
                    }}
                  >
                    {agencyConfig.name}
                  </h5>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                {/* <NotificationButton
                  feedId={`agency-${agencyId}`}
                  feedName={`${agencyConfig.name} Updates`}
                  branch="Executive"
                  topic={agencyConfig.topic}
                  variant="outline-light"
                  size="sm"
                  showLabel={false}
                  onNotificationStateChange={handleAgencyNotificationChange}
                /> */}
                <Dropdown drop="down-centered">
                  <Dropdown.Toggle id="searchCountry"><Link45deg /></Dropdown.Toggle>
                  <Dropdown.Menu id="docsDropdown">
                    <Dropdown.Item 
                      id="docsDropdownItem" 
                      onClick={(e) => { 
                        e.preventDefault(); 
                        openCapacitorSite(agencyConfig.officialURL, "_blank"); 
                      }}
                    >
                      Official Site
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            
            <div id="billText" style={{ padding: '4px 10px 8px' }}>
              <div className="agency-name-text" style={{ fontSize: '13px', fontWeight: '600' }}>
                {agencyConfig.name}
              </div>
              <div className="department-link-text" style={{ fontSize: '11px', marginTop: '2px' }}>
                {departmentInfo.name}
              </div>
              <div className="mission-text" style={{ fontSize: '12px', marginTop: '6px' }}>
                Mission Statement: {agencyConfig.missionStatement}
              </div>
            </div>
          </div>

          <div className="mobile-feeds-container">
            {mobileFeedsData.length > 0 ? (
              <MobileFeedCarousel feeds={mobileFeedsData} />
            ) : (
              <div className="empty-feeds-message">
                <p>No feeds configured for this agency.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- DESKTOP LAYOUT ----------
  const agencyFeeds = [
    {
      id: agencyConfig.id,
      name: `${agencyConfig.shortName} Documents`,
      apiParams: agencyConfig.apiParams,
      feedBranch: 'Executive',
      feedTopic: agencyConfig.topic,
      buttonName: agencyConfig.buttonName || 'Document'
    }
  ];

  const feedRows = [];
  for (let i = 0; i < agencyFeeds.length; i += 2) {
    feedRows.push(agencyFeeds.slice(i, i + 2));
  }

  return (
    <div id='homebg' className="agency-page-root">
      {/* Desktop Agency Header */}
      <div style={{ position: 'relative', padding: '30px 0' }}>
        <div id='profileImage' style={{ 
          width: '130px', 
          height: '130px', 
          borderRadius: '100%', 
          overflow: 'hidden', 
          marginLeft: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AgencyIcon 
            image={agencyConfig.image} 
            name={agencyConfig.name} 
            size={70}
          />
        </div>

        <div style={{ 
          position: 'absolute', 
          top: '0', 
          left: '250px', 
          display: 'flex', 
          flexDirection: 'column',
          width: 'calc(100% - 250px)'
        }}>
          <div style={{ fontSize: '20px' }}> 
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{ margin: 0 }}>
                  {agencyConfig.shortName}
                  <span className="agency-subtitle" style={{ 
                    fontSize: '16px', 
                    marginLeft: '10px',
                    fontWeight: 'normal'
                  }}>
                    {agencyConfig.name}
                  </span>
                </h3>
                <div className="parent-department" style={{ 
                  fontSize: '14px', 
                  marginTop: '5px' 
                }}>
                  {departmentInfo.name}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {/* <NotificationButton
                  feedId={`agency-${agencyId}`}
                  feedName={`${agencyConfig.name} Updates`}
                  branch="Executive"
                  topic={agencyConfig.topic}
                  variant="outline-light"
                  size="sm"
                  showLabel={true}
                  onNotificationStateChange={handleAgencyNotificationChange}
                /> */}
              </div>
            </div>
            
            <div className="mission-text" style={{ fontSize: '16px', width: '80%' }}>
              <span>{agencyConfig.missionStatement}</span>
              <br />
              <div style={{ fontSize: '18px', marginTop: '10px' }}>
                <span>
                  <Link 
                    onClick={(e) => {
                      e.preventDefault();
                      openCapacitorSite(agencyConfig.officialURL, "_blank");
                    }}
                  >
                    Visit Official Website
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <br /><br />

      {/* Agency Feeds - matching MainDepartmentPage layout */}
      {feedRows.map((row, rowIndex) => (
        <div key={rowIndex} id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                {row.map((feed, colIndex) => (
                  <Col key={colIndex} id={colIndex % 2 === 0 ? 'legislativeColLeft' : 'legislativeColRight'}>
                    <div id={colIndex % 2 === 0 ? 'columnLefttPadding' : 'columnRightPadding'}>
                      <FeedSection
                        title={feed.name}
                        apiEndpoint={feed.apiParams}
                        feedName={feed.name}
                        branch={feed.feedBranch}
                        topic={feed.feedTopic || 'N/A'}
                        buttonName={feed.buttonName}
                        maxItems={maxItemsPerFeed}
                      />
                    </div>
                  </Col>
                ))}

                {row.length === 1 && (
                  <Col id='legislativeColRight'>
                    <div id='columnRightPadding'></div>
                  </Col>
                )}
              </Row>
            </Container>
          </Container>
        </div>
      ))}
    </div>
  );
};

export default AgencyPage;